const bespokeRoiCalculatorHtml = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Bespoke ROI Calculator</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
:root{
  --bg:#0F1923;--card:#1A2332;--card2:#1E2D3D;--card3:#243447;
  --mint:#29BB89;--mint-bg:rgba(41,187,137,.12);
  --coral:#F87171;--coral-bg:rgba(248,113,113,.10);
  --blue:#60A5FA;--blue-bg:rgba(96,165,250,.10);
  --amber:#FBBF24;--amber-bg:rgba(251,191,36,.10);
  --purple:#A78BFA;--purple-bg:rgba(167,139,250,.10);
  --white:#F8FAFC;--gray:#94A3B8;--mid:#64748B;--dark-text:#CBD5E1;
  --border:#334155;--input-bg:#0D1520;
  --radius:12px;--radius-sm:8px;
}
body{font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:var(--bg);color:var(--dark-text);line-height:1.6;min-height:100vh}
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

/* ── Layout ── */
.container{max-width:1200px;margin:0 auto;padding:24px 20px}
.hero{text-align:center;padding:48px 20px 32px;background:linear-gradient(135deg,#0F1923 0%,#1A2332 50%,#162032 100%)}
.hero h1{font-size:2.2rem;font-weight:800;color:var(--white);margin-bottom:8px;letter-spacing:-0.5px}
.hero .subtitle{font-size:1.05rem;color:var(--gray);max-width:640px;margin:0 auto}
.hero .powered{font-size:.8rem;color:var(--mid);margin-top:12px}

/* ── Selector Cards ── */
.selector-row{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px}
@media(max-width:700px){.selector-row{grid-template-columns:1fr}}
.selector-card{background:var(--card);border:1px solid var(--border);border-radius:var(--radius);padding:20px}
.selector-card h3{font-size:.85rem;font-weight:600;color:var(--gray);text-transform:uppercase;letter-spacing:.5px;margin-bottom:12px}
.selector-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:8px}
.selector-btn{background:var(--card2);border:2px solid var(--border);border-radius:var(--radius-sm);padding:10px 12px;color:var(--dark-text);font-size:.85rem;font-weight:500;cursor:pointer;transition:all .2s;text-align:center}
.selector-btn:hover{border-color:var(--blue);background:var(--blue-bg)}
.selector-btn.active{border-color:var(--mint);background:var(--mint-bg);color:var(--mint);font-weight:600}
.selection-summary{margin-top:16px;padding:12px 16px;background:var(--card2);border-radius:var(--radius-sm);font-size:.9rem;color:var(--gray);display:none}
.selection-summary.visible{display:block}
.selection-summary strong{color:var(--mint)}

/* ── Sections ── */
.section{background:var(--card);border:1px solid var(--border);border-radius:var(--radius);padding:24px;margin-bottom:20px}
.section-header{display:flex;align-items:center;gap:10px;margin-bottom:18px;padding-bottom:12px;border-bottom:1px solid var(--border)}
.section-icon{width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:1rem;flex-shrink:0}
.section-header h2{font-size:1.05rem;font-weight:700;color:var(--white)}
.section-header .section-desc{font-size:.8rem;color:var(--mid);margin-left:auto}

/* ── Input Rows ── */
.input-group{display:grid;gap:2px}
.input-row{display:grid;grid-template-columns:1fr 140px 90px;gap:12px;align-items:center;padding:10px 14px;border-radius:var(--radius-sm);transition:background .15s}
.input-row:hover{background:rgba(255,255,255,.02)}
.input-row.computed{background:rgba(41,187,137,.04)}
.input-row.highlight{background:var(--mint-bg);border:1px solid rgba(41,187,137,.2)}
.input-row.negative{background:var(--coral-bg);border:1px solid rgba(248,113,113,.15)}
.input-row.subtotal{background:var(--blue-bg);border:1px solid rgba(96,165,250,.15)}

.input-label{font-size:.88rem;color:var(--dark-text)}
.input-label small{display:block;font-size:.75rem;color:var(--mid);margin-top:2px}
.input-unit{font-size:.78rem;color:var(--mid);text-align:right;white-space:nowrap}

input[type="number"]{
  background:var(--input-bg);border:1px solid var(--border);border-radius:6px;
  padding:8px 10px;color:var(--blue);font-size:.95rem;font-weight:600;
  width:100%;text-align:right;font-family:inherit;
  transition:border-color .2s;
}
input[type="number"]:focus{outline:none;border-color:var(--blue)}
input[type="number"]::-webkit-inner-spin-button{opacity:1}

.computed-value{
  background:transparent;border:1px solid transparent;border-radius:6px;
  padding:8px 10px;color:var(--white);font-size:.95rem;font-weight:700;
  text-align:right;min-width:120px;
}
.computed-value.mint{color:var(--mint)}
.computed-value.coral{color:var(--coral)}
.computed-value.blue{color:var(--blue)}
.computed-value.amber{color:var(--amber)}
.computed-value.big{font-size:1.15rem}

/* ── Result Cards ── */
.results-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:16px;margin-top:16px}
.result-card{background:var(--card2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:18px;text-align:center}
.result-card .label{font-size:.78rem;color:var(--mid);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px}
.result-card .value{font-size:1.6rem;font-weight:800;color:var(--mint)}
.result-card .value.blue{color:var(--blue)}
.result-card .value.amber{color:var(--amber)}
.result-card .value.purple{color:var(--purple)}
.result-card .sub{font-size:.78rem;color:var(--gray);margin-top:4px}

/* ── Scale-Up Section ── */
.scale-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
@media(max-width:700px){.scale-grid{grid-template-columns:1fr}}

/* ── Methodology ── */
.methodology{background:var(--card);border:1px solid var(--border);border-radius:var(--radius);padding:24px;margin-bottom:20px}
.methodology h2{font-size:1.05rem;font-weight:700;color:var(--white);margin-bottom:16px;padding-bottom:12px;border-bottom:1px solid var(--border)}
.method-block{margin-bottom:20px}
.method-block h3{font-size:.9rem;font-weight:600;color:var(--blue);margin-bottom:8px}
.method-block p{font-size:.85rem;color:var(--gray);margin-bottom:6px}
.method-block .formula{background:var(--card2);border-radius:6px;padding:10px 14px;font-family:'Fira Code',monospace;font-size:.82rem;color:var(--amber);margin:8px 0}

/* ── Divider ── */
.divider{width:100%;height:1px;background:var(--border);margin:4px 0}

/* ── Toast / Note ── */
.note-bar{background:var(--amber-bg);border:1px solid rgba(251,191,36,.2);border-radius:var(--radius-sm);padding:12px 16px;margin-bottom:20px;font-size:.85rem;color:var(--amber);display:flex;align-items:flex-start;gap:10px}
.note-bar .icon{font-size:1.1rem;flex-shrink:0}

/* ── Footer ── */
.footer{text-align:center;padding:32px 20px;color:var(--mid);font-size:.8rem}
.footer a{color:var(--blue);text-decoration:none}

/* ── Toggle / Expand ── */
.expand-btn{background:var(--card2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 16px;color:var(--blue);font-size:.82rem;cursor:pointer;transition:all .2s;margin-top:12px}
.expand-btn:hover{background:var(--blue-bg);border-color:var(--blue)}
.hidden{display:none}

/* ── Floating Stats Panel ── */
.floating-stats{
  position:fixed;bottom:0;left:0;right:0;z-index:1000;
  background:rgba(15,25,35,.96);backdrop-filter:blur(12px);
  border-top:1px solid var(--border);
  padding:10px 20px;
  transform:translateY(100%);
  transition:transform .35s cubic-bezier(.4,0,.2,1);
  pointer-events:none;
}
.floating-stats.visible{transform:translateY(0);pointer-events:auto}
.floating-stats-inner{
  max-width:1200px;margin:0 auto;
  display:grid;grid-template-columns:repeat(6,1fr) auto repeat(2,1fr);gap:10px;
  align-items:center;
}
@media(max-width:900px){.floating-stats-inner{grid-template-columns:repeat(4,1fr);gap:6px}.float-divider{display:none}}
@media(max-width:500px){.floating-stats-inner{grid-template-columns:repeat(2,1fr);gap:6px}}
.float-card{text-align:center;padding:6px 4px}
.float-card .fl-label{font-size:.65rem;color:var(--mid);text-transform:uppercase;letter-spacing:.3px;margin-bottom:2px;white-space:nowrap}
.float-card .fl-value{font-size:1.1rem;font-weight:800;color:var(--mint);white-space:nowrap}
.float-card .fl-value.fl-blue{color:var(--blue)}
.float-card .fl-value.fl-amber{color:var(--amber)}
.float-card .fl-value.fl-purple{color:var(--purple)}
.float-divider{width:1px;background:var(--border);align-self:stretch;margin:2px 0}
</style>
</head>
<body>

<div class="hero">
  <h1>Bespoke Upsell ROI Calculator</h1>
  <p class="subtitle">See how AI-powered upsell coaching translates into dollars for your restaurant — per server, per location, and across your entire operation.</p>
  <p class="powered">Powered by Bespoke</p>
</div>

<div class="container">

<!-- ═══════════════════════════════════════ -->
<!-- SELECTORS                              -->
<!-- ═══════════════════════════════════════ -->
<div class="selector-row">
  <div class="selector-card">
    <h3>Restaurant Format</h3>
    <div class="selector-grid" id="format-grid"></div>
  </div>
  <div class="selector-card">
    <h3>Cuisine Type</h3>
    <div class="selector-grid" id="cuisine-grid"></div>
  </div>
</div>

<div class="selection-summary" id="selection-summary"></div>

<div class="note-bar">
  <span class="icon">&#9432;</span>
  <span>Default values below are populated from industry research for your selected format &amp; cuisine. <strong>Every blue number is editable</strong> — adjust anything to match your restaurant.</span>
</div>

<!-- ═══════════════════════════════════════ -->
<!-- SECTION 1: YOUR RESTAURANT             -->
<!-- ═══════════════════════════════════════ -->
<div class="section">
  <div class="section-header">
    <div class="section-icon" style="background:var(--blue-bg);color:var(--blue)">&#127860;</div>
    <h2>Your Restaurant (Per Server)</h2>
    <span class="section-desc">All figures are for a single server</span>
  </div>
  <div class="input-group">
    <div class="input-row">
      <div class="input-label">Average shifts per server per month</div>
      <input type="number" id="shifts_mo" step="0.1" value="8.4">
      <div class="input-unit">shifts/mo</div>
    </div>
    <div class="input-row">
      <div class="input-label">Average tables (orders) per shift</div>
      <input type="number" id="tables_shift" step="0.1" value="7.9">
      <div class="input-unit">tables/shift</div>
    </div>
    <div class="input-row">
      <div class="input-label">Average party size</div>
      <input type="number" id="party_size" step="0.1" value="2.5">
      <div class="input-unit">guests/table</div>
    </div>
    <div class="input-row">
      <div class="input-label">Current average order value (AOV)</div>
      <input type="number" id="current_aov" step="0.01" value="98.16">
      <div class="input-unit">$/order</div>
    </div>
    <div class="input-row computed">
      <div class="input-label">Average per-person spend<small>AOV &divide; party size</small></div>
      <div class="computed-value" id="per_person_spend">$39.26</div>
      <div class="input-unit">$/person</div>
    </div>
    <div class="input-row">
      <div class="input-label">Average tip percentage</div>
      <input type="number" id="tip_pct" step="0.1" value="18">
      <div class="input-unit">%</div>
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════ -->
<!-- SECTION 2: FOOD UPSELL MODEL           -->
<!-- ═══════════════════════════════════════ -->
<div class="section">
  <div class="section-header">
    <div class="section-icon" style="background:var(--mint-bg);color:var(--mint)">&#9650;</div>
    <h2>Food Upsell Model</h2>
    <span class="section-desc">How improved server greetings lift AOV</span>
  </div>
  <div class="input-group">
    <div class="input-row">
      <div class="input-label">AOV impact per 1 pp of upsell quality<small>From regression: each percentage point of upsell quality improvement = this much more AOV</small></div>
      <input type="number" id="coeff_per_pp" step="0.001" value="0.2345">
      <div class="input-unit">$/order per pp</div>
    </div>
    <div class="input-row computed">
      <div class="input-label">Full coefficient (per 100pp)<small>The regression coefficient — total AOV lift if a server went from 0% to 100% upsell quality</small></div>
      <div class="computed-value blue" id="full_coeff">$23.45</div>
      <div class="input-unit">$/order</div>
    </div>
    <div class="divider"></div>
    <div class="input-row">
      <div class="input-label">Current average upsell quality score<small>Median server score: weighted blend of premium-mention, name-item, describe-item</small></div>
      <input type="number" id="current_quality" step="0.1" value="36.5">
      <div class="input-unit">%</div>
    </div>
    <div class="input-row">
      <div class="input-label">Target upsell quality score with Bespoke<small>Top-quartile server performance is a realistic target</small></div>
      <input type="number" id="target_quality" step="0.1" value="55.0">
      <div class="input-unit">%</div>
    </div>
    <div class="input-row subtotal">
      <div class="input-label">Improvement in upsell quality</div>
      <div class="computed-value blue" id="quality_improvement">18.5 pp</div>
      <div class="input-unit">pp improvement</div>
    </div>
    <div class="input-row highlight">
      <div class="input-label"><strong>Effective food AOV lift per order</strong><small>Improvement pp &times; coefficient per pp</small></div>
      <div class="computed-value mint big" id="food_aov_lift">$4.34</div>
      <div class="input-unit">$/order</div>
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════ -->
<!-- SECTION 3: DRINK PROMO MODEL           -->
<!-- ═══════════════════════════════════════ -->
<div class="section">
  <div class="section-header">
    <div class="section-icon" style="background:var(--purple-bg);color:var(--purple)">&#127864;</div>
    <h2>Drink Promo Model</h2>
    <span class="section-desc">How more drink promotions lift revenue</span>
  </div>
  <div class="input-group">
    <div class="input-row">
      <div class="input-label">Current average drink promos per shift</div>
      <input type="number" id="current_drink_promos" step="0.1" value="1.9">
      <div class="input-unit">promos/shift</div>
    </div>
    <div class="input-row">
      <div class="input-label">Potential drink promos per shift (trained)</div>
      <input type="number" id="target_drink_promos" step="0.1" value="3.5">
      <div class="input-unit">promos/shift</div>
    </div>
    <div class="input-row">
      <div class="input-label">% of that drink improvement Bespoke delivers<small>Conservative estimate of how much of the gap Bespoke can close</small></div>
      <input type="number" id="drink_realization" step="1" value="50">
      <div class="input-unit">%</div>
    </div>
    <div class="input-row subtotal">
      <div class="input-label">Effective extra drink promos per shift<small>(Target &minus; Current) &times; realization %</small></div>
      <div class="computed-value blue" id="extra_drink_promos">0.8</div>
      <div class="input-unit">extra promos</div>
    </div>
    <div class="input-row">
      <div class="input-label">Average revenue per drink promo</div>
      <input type="number" id="rev_per_promo" step="0.5" value="12.00">
      <div class="input-unit">$/promo</div>
    </div>
    <div class="input-row highlight">
      <div class="input-label"><strong>Effective extra drink revenue per shift</strong><small>Extra promos &times; revenue per promo</small></div>
      <div class="computed-value mint big" id="extra_drink_rev_shift">$9.60</div>
      <div class="input-unit">$/shift</div>
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════ -->
<!-- SECTION 4: PER-SERVER WATERFALL        -->
<!-- ═══════════════════════════════════════ -->
<div class="section">
  <div class="section-header">
    <div class="section-icon" style="background:var(--amber-bg);color:var(--amber)">&#128200;</div>
    <h2>The Math: One Server's Impact</h2>
    <span class="section-desc">Combined food + drink uplift per server</span>
  </div>
  <div class="input-group">
    <div class="input-row">
      <div class="input-label">A. Food upsell lift per table</div>
      <div class="computed-value" id="wf_food_table">$4.34</div>
      <div class="input-unit">$/table</div>
    </div>
    <div class="input-row">
      <div class="input-label">B. Food upsell lift per shift<small>A &times; tables per shift</small></div>
      <div class="computed-value" id="wf_food_shift">$34.27</div>
      <div class="input-unit">$/shift</div>
    </div>
    <div class="input-row">
      <div class="input-label">C. Drink promo lift per shift</div>
      <div class="computed-value" id="wf_drink_shift">$9.60</div>
      <div class="input-unit">$/shift</div>
    </div>
    <div class="input-row subtotal">
      <div class="input-label"><strong>D. Combined extra revenue per shift</strong></div>
      <div class="computed-value blue big" id="wf_combined_shift">$43.87</div>
      <div class="input-unit">$/shift</div>
    </div>
    <div class="input-row highlight">
      <div class="input-label"><strong>E. Extra revenue per server per month</strong><small>D &times; shifts per month</small></div>
      <div class="computed-value mint big" id="wf_server_mo">$368.53</div>
      <div class="input-unit">$/server/mo</div>
    </div>
    <div class="input-row highlight">
      <div class="input-label"><strong>F. Extra revenue per server per year</strong></div>
      <div class="computed-value mint big" id="wf_server_yr">$4,422</div>
      <div class="input-unit">$/server/yr</div>
    </div>
  </div>

  <!-- Tip Impact -->
  <div style="margin-top:18px;padding-top:16px;border-top:1px solid var(--border)">
    <h3 style="font-size:.88rem;color:var(--gray);margin-bottom:12px">Server Tip Impact (food + drinks combined)</h3>
    <div class="input-group">
      <div class="input-row">
        <div class="input-label">Extra tips per shift<small>Combined lift &times; tip %</small></div>
        <div class="computed-value" id="tips_shift">$7.90</div>
        <div class="input-unit">$/shift</div>
      </div>
      <div class="input-row highlight">
        <div class="input-label"><strong>Extra tips per server per month</strong></div>
        <div class="computed-value mint" id="tips_mo">$66.33</div>
        <div class="input-unit">$/server/mo</div>
      </div>
      <div class="input-row highlight">
        <div class="input-label"><strong>Extra tips per server per year</strong></div>
        <div class="computed-value mint" id="tips_yr">$796</div>
        <div class="input-unit">$/server/yr</div>
      </div>
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════ -->
<!-- SECTION 5: BESPOKE COST & ROI          -->
<!-- ═══════════════════════════════════════ -->
<div class="section">
  <div class="section-header">
    <div class="section-icon" style="background:var(--mint-bg);color:var(--mint)">&#128176;</div>
    <h2>Bespoke Cost & Per-Server ROI</h2>
  </div>
  <div class="input-group">
    <div class="input-row">
      <div class="input-label">Bespoke monthly cost per server</div>
      <input type="number" id="bespoke_cost" step="1" value="50">
      <div class="input-unit">$/server/mo</div>
    </div>
    <div class="input-row highlight">
      <div class="input-label"><strong>Net monthly revenue gain per server</strong></div>
      <div class="computed-value mint big" id="net_per_server_mo">$318.53</div>
      <div class="input-unit">$/server/mo</div>
    </div>
    <div class="input-row highlight">
      <div class="input-label"><strong>Per-server revenue ROI</strong></div>
      <div class="computed-value mint big" id="per_server_roi">7.4x</div>
      <div class="input-unit">return</div>
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════ -->
<!-- SECTION 6: SCALE UP                    -->
<!-- ═══════════════════════════════════════ -->
<div class="section" id="scale-up-section">
  <div class="section-header">
    <div class="section-icon" style="background:var(--amber-bg);color:var(--amber)">&#127959;</div>
    <h2>Scale Up: Location & Chain Impact</h2>
    <span class="section-desc">Multiply by your servers and locations</span>
  </div>
  <div class="scale-grid">
    <div>
      <div class="input-group">
        <div class="input-row">
          <div class="input-label">Servers per location</div>
          <input type="number" id="servers_per_loc" step="1" value="34">
          <div class="input-unit">servers</div>
        </div>
        <div class="input-row">
          <div class="input-label">Number of locations</div>
          <input type="number" id="num_locations" step="1" value="1">
          <div class="input-unit">locations</div>
        </div>
        <div class="input-row computed">
          <div class="input-label">Total servers across chain</div>
          <div class="computed-value" id="total_servers">34</div>
          <div class="input-unit">servers</div>
        </div>
      </div>
    </div>
    <div>
      <div class="results-grid" style="margin-top:0">
        <div class="result-card">
          <div class="label">Monthly Revenue Uplift</div>
          <div class="value" id="chain_mo_rev">$12,530</div>
        </div>
        <div class="result-card">
          <div class="label">Annual Revenue Uplift</div>
          <div class="value" id="chain_yr_rev">$150,359</div>
        </div>
        <div class="result-card">
          <div class="label">Monthly Bespoke Cost</div>
          <div class="value blue" id="chain_cost">$1,700</div>
        </div>
        <div class="result-card">
          <div class="label">Net Monthly Gain</div>
          <div class="value" id="chain_net_mo">$10,830</div>
        </div>
        <div class="result-card">
          <div class="label">Annual Net Gain</div>
          <div class="value" id="chain_net_yr">$129,959</div>
        </div>
        <div class="result-card">
          <div class="label">Revenue ROI</div>
          <div class="value amber" id="chain_roi">7.4x</div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════ -->
<!-- SECTION 7: MARGIN-ADJUSTED VIEW        -->
<!-- ═══════════════════════════════════════ -->
<div class="section">
  <div class="section-header">
    <div class="section-icon" style="background:var(--purple-bg);color:var(--purple)">&#128202;</div>
    <h2>Margin-Adjusted View: What You Actually Keep</h2>
    <span class="section-desc">After cost of goods</span>
  </div>
  <div class="input-group">
    <div class="input-row">
      <div class="input-label">Food cost as % of food revenue</div>
      <input type="number" id="food_cost_pct" step="0.5" value="30">
      <div class="input-unit">%</div>
    </div>
    <div class="input-row">
      <div class="input-label">Beverage cost as % of beverage revenue</div>
      <input type="number" id="bev_cost_pct" step="0.5" value="20">
      <div class="input-unit">%</div>
    </div>
    <div class="divider"></div>
    <div class="input-row">
      <div class="input-label">Food uplift gross profit (chain/month)<small>Food revenue uplift &times; (1 &minus; food cost %)</small></div>
      <div class="computed-value mint" id="food_gp">$6,852</div>
      <div class="input-unit">$/month</div>
    </div>
    <div class="input-row">
      <div class="input-label">Drink uplift gross profit (chain/month)<small>Drink revenue uplift &times; (1 &minus; bev cost %)</small></div>
      <div class="computed-value mint" id="drink_gp">$2,194</div>
      <div class="input-unit">$/month</div>
    </div>
    <div class="input-row subtotal">
      <div class="input-label"><strong>Combined gross profit uplift</strong></div>
      <div class="computed-value blue big" id="combined_gp">$9,045</div>
      <div class="input-unit">$/month</div>
    </div>
    <div class="input-row negative">
      <div class="input-label">Less: Bespoke cost</div>
      <div class="computed-value coral" id="margin_bespoke_cost">($1,700)</div>
      <div class="input-unit">$/month</div>
    </div>
    <div class="input-row highlight">
      <div class="input-label"><strong>Net profit uplift after Bespoke</strong></div>
      <div class="computed-value mint big" id="net_profit">$7,345</div>
      <div class="input-unit">$/month</div>
    </div>
    <div class="input-row highlight">
      <div class="input-label"><strong>Margin-adjusted ROI</strong></div>
      <div class="computed-value mint big" id="margin_roi">5.3x</div>
      <div class="input-unit">return</div>
    </div>
    <div class="input-row highlight">
      <div class="input-label"><strong>Annual net profit uplift</strong></div>
      <div class="computed-value mint big" id="annual_net_profit">$88,141</div>
      <div class="input-unit">$/year</div>
    </div>
  </div>
</div>

<!-- ═══════════════════════════════════════ -->
<!-- METHODOLOGY                            -->
<!-- ═══════════════════════════════════════ -->
<div class="methodology">
  <h2>How This Calculator Works</h2>

  <div class="method-block">
    <h3>The Upsell Quality Score</h3>
    <p>Bespoke transcribes and scores every server-guest interaction. The <strong>upsell quality score</strong> (0-100%) is a weighted composite of three measurable behaviors:</p>
    <div class="formula">upsell_quality = 25% &times; premium_mention + 35% &times; name_specific_item + 40% &times; describe_item</div>
    <p><strong>Premium mention (25%):</strong> Did the server mention any premium item? The lowest bar — just saying "we have wagyu" counts.</p>
    <p><strong>Name item (35%):</strong> Did the server call out specific items by name? E.g., "the Korean taco sampler" vs. "our appetizers."</p>
    <p><strong>Describe item (40%):</strong> Did the server describe what makes it appealing? E.g., "the wagyu is really tender, it melts on the grill." Hardest habit but strongest AOV predictor.</p>
  </div>

  <div class="method-block">
    <h3>The Regression Coefficient</h3>
    <p>An OLS linear regression was run on 384 shifts of POS and transcript data:</p>
    <div class="formula">AOV = &beta;<sub>0</sub> + $23.45 &times; upsell_quality + &beta;<sub>2</sub> &times; location + &epsilon;</div>
    <p>The coefficient of <strong>$23.45 per 1.0 unit</strong> (i.e., <strong>$0.2345 per percentage point</strong>) means that for every 1pp improvement in a server's upsell quality, their average order goes up by ~$0.23. The result is statistically significant (p&lt;0.01).</p>
  </div>

  <div class="method-block">
    <h3>Example Calculation</h3>
    <p>If a median server (36.5% quality) improves to the top-quartile level (55%):</p>
    <div class="formula">18.5 pp &times; $0.2345/pp = $4.34 more per order<br>$4.34 &times; 7.9 tables &times; 8.4 shifts = $288/server/month (food only)</div>
    <p>Add drink promo lift and you get a combined uplift that flows through the waterfall above.</p>
  </div>

  <div class="method-block">
    <h3>Why the Target Score Is Realistic</h3>
    <p>The default 55% target is not aspirational — it's the <strong>existing top-quartile average</strong>. A quarter of servers already achieve it naturally. Reaching it requires three specific habit changes: asking "been here before?" every time, naming premium items by name instead of defaulting to lower-margin items, and spending 60 seconds (not 30) on the greeting. These are vocabulary and timing changes, not personality changes — exactly the kind of behavior an AI coaching app reinforces.</p>
  </div>

  <div class="method-block">
    <h3>Default Values by Format &amp; Cuisine</h3>
    <p>When you select a restaurant format and cuisine type above, the calculator populates industry-typical defaults for average check, party size, food cost %, beverage cost %, drink prices, tables per shift, and tip rates. These are compiled from National Restaurant Association data, industry benchmarks, and public financial reports. They represent reasonable starting points — you should adjust them to match your actual restaurant.</p>
  </div>
</div>

<div class="methodology">
  <h2>Research-Backed Default Matrix</h2>
  <p style="font-size:.85rem;color:var(--gray);margin-bottom:18px;">The 48 format &times; cuisine preset combinations are built from the following industry research. Every value is editable — these are starting points, not assumptions.</p>

  <div class="method-block">
    <h3>Per-Person Spend</h3>
    <p>Ranges from <strong>~$16/person</strong> (Fast Casual Chinese) to <strong>~$86/person</strong> (Fine Dining Steakhouse), calibrated against National Restaurant Association benchmarks, publicly reported chain average checks, and cuisine-specific dining surveys. Fine dining multiplies the casual base by 1.8&times;; upscale casual by 1.3&times;; fast casual by 0.65&times;.</p>
  </div>

  <div class="method-block">
    <h3>Food Cost %</h3>
    <p>Ranges from <strong>23%</strong> (Fast Casual Mexican — rice, beans, tortillas are low-COGS staples) to <strong>38%</strong> (Fine Dining Steakhouse — premium aged cuts carry higher ingredient cost). The NRA reports a median food cost of 32% across full-service restaurants; we adjust &plusmn;3-5 points by cuisine based on protein intensity, import costs, and preparation complexity.</p>
  </div>

  <div class="method-block">
    <h3>Beverage Cost %</h3>
    <p>Ranges from <strong>16%</strong> (Fast Casual formats with fountain drinks and basic beer) to <strong>26%</strong> (Fine Dining French and Italian, where wine-heavy programs carry 35-45% pour cost on bottles). Industry average pour cost is 18-24%. Cocktail-forward cuisines (Japanese, American) run 20-22%; wine-forward (French, Italian, Mediterranean) run 24-26%.</p>
  </div>

  <div class="method-block">
    <h3>Average Drink Price</h3>
    <p>Ranges from <strong>$6</strong> (Fast Casual) to <strong>$20</strong> (Fine Dining), reflecting the spectrum from domestic drafts and house wines to craft cocktails, premium sake, and curated wine pairings. Casual dining centers around $10-14 depending on the beverage program strength of each cuisine.</p>
  </div>

  <div class="method-block">
    <h3>Upsell Coefficient ($/pp)</h3>
    <p>Calibrated proportionally to each cuisine's <strong>premium upcharge spread</strong> — the price gap between the cheapest and most expensive items a server can steer a guest toward. Steakhouses have the highest coefficient (<strong>$0.32/pp</strong>) because the gap between a chicken entrée and an A5 wagyu can be $40+. Mexican has the lowest (<strong>$0.10/pp</strong>) because the price spread between a basic taco plate and a premium option is typically under $10. The baseline calibration comes from the empirically measured $0.23/pp coefficient in Korean BBQ data (premium upcharge ~$20/person).</p>
  </div>

  <div class="method-block">
    <h3>ROI Range Across All Combinations</h3>
    <p>Every single format &times; cuisine combination produces a <strong>positive ROI</strong>, ranging from <strong>3.4&times;</strong> (Casual Mexican — lowest ticket, narrowest premium spread) to <strong>8.4&times;</strong> (Casual Steakhouse — biggest premium lever with high table volume). Fine dining formats show strong per-shift impact but fewer tables; fast casual shows lower per-table impact but high volume. The math self-balances across formats because higher-ticket restaurants have bigger dollar lifts per table while higher-volume restaurants have more tables to lift.</p>
  </div>

  <div class="method-block">
    <h3>Sources</h3>
    <p style="font-size:.82rem;color:var(--mid);">National Restaurant Association Operations Reports (2024-2025) &middot; Toast Restaurant Industry Data &middot; Lightspeed Profit Margin Guide &middot; BackBar Liquor Cost Benchmarks &middot; Restroworks Sales & Cost Statistics &middot; BusinessDojo Cuisine Profitability Reports &middot; IBIS World Industry Analysis &middot; Bespoke pilot data (384 shifts, 2,775 server-guest interactions)</p>
  </div>
</div>

<div class="footer">
  <p>&copy; 2026 Bespoke &middot; AI-Powered Server Coaching</p>
  <p style="margin-top:4px">Default values sourced from NRA Restaurant Operations Data, industry benchmarks, and public financial reports.</p>
</div>

</div><!-- end container -->

<script>
// ═══════════════════════════════════════════
// DEFAULT DATA MATRIX
// ═══════════════════════════════════════════

// Format defaults
const FORMATS = {
  "Fine Dining": {
    id:"fine", tables_shift:4.0, party_size:2.5, tip_pct:22,
    shifts_mo:8, current_drink_promos:2.5, target_drink_promos:4.5,
    drink_realization:50, servers_per_loc:20,
    // Applied as multipliers/overrides to cuisine base
    aov_mult:1.8, food_cost_add:3, bev_cost_add:-2, drink_price_add:6
  },
  "Upscale Casual": {
    id:"upscale", tables_shift:5.5, party_size:2.8, tip_pct:20,
    shifts_mo:8.5, current_drink_promos:2.2, target_drink_promos:4.0,
    drink_realization:50, servers_per_loc:25,
    aov_mult:1.3, food_cost_add:1, bev_cost_add:0, drink_price_add:3
  },
  "Casual Dining": {
    id:"casual", tables_shift:6.5, party_size:3.0, tip_pct:18,
    shifts_mo:8.5, current_drink_promos:1.9, target_drink_promos:3.5,
    drink_realization:50, servers_per_loc:30,
    aov_mult:1.0, food_cost_add:0, bev_cost_add:0, drink_price_add:0
  },
  "Fast Casual": {
    id:"fastcasual", tables_shift:9.0, party_size:2.2, tip_pct:12,
    shifts_mo:9, current_drink_promos:1.0, target_drink_promos:2.5,
    drink_realization:40, servers_per_loc:15,
    aov_mult:0.65, food_cost_add:-3, bev_cost_add:-2, drink_price_add:-3
  }
};

// Cuisine base defaults (at casual dining level; base_aov = total order value)
const CUISINES = {
  "American": {
    id:"american", base_aov:88, food_cost:30, bev_cost:22,
    drink_price:11, premium_upcharge:12, coeff:0.15,
    desc:"Burgers, steaks, comfort classics"
  },
  "Korean": {
    id:"korean", base_aov:98, food_cost:30, bev_cost:20,
    drink_price:12, premium_upcharge:20, coeff:0.23,
    desc:"BBQ, AYCE, Korean specialties"
  },
  "Japanese": {
    id:"japanese", base_aov:105, food_cost:32, bev_cost:22,
    drink_price:13, premium_upcharge:18, coeff:0.21,
    desc:"Sushi, ramen, izakaya, omakase"
  },
  "Italian": {
    id:"italian", base_aov:86, food_cost:28, bev_cost:24,
    drink_price:12, premium_upcharge:14, coeff:0.17,
    desc:"Pasta, pizza, regional Italian"
  },
  "French": {
    id:"french", base_aov:105, food_cost:33, bev_cost:26,
    drink_price:14, premium_upcharge:22, coeff:0.25,
    desc:"Bistro, brasserie, haute cuisine"
  },
  "Mexican": {
    id:"mexican", base_aov:62, food_cost:26, bev_cost:18,
    drink_price:10, premium_upcharge:8, coeff:0.10,
    desc:"Tacos, enchiladas, Oaxacan, Mex"
  },
  "Steakhouse": {
    id:"steakhouse", base_aov:120, food_cost:35, bev_cost:22,
    drink_price:14, premium_upcharge:28, coeff:0.32,
    desc:"Premium cuts, chops, surf & turf"
  },
  "Seafood": {
    id:"seafood", base_aov:102, food_cost:34, bev_cost:22,
    drink_price:12, premium_upcharge:16, coeff:0.19,
    desc:"Fresh catch, oyster bar, coastal"
  },
  "Indian": {
    id:"indian", base_aov:62, food_cost:28, bev_cost:20,
    drink_price:10, premium_upcharge:10, coeff:0.12,
    desc:"Tandoori, curry, thali, biryani"
  },
  "Thai": {
    id:"thai", base_aov:60, food_cost:27, bev_cost:20,
    drink_price:10, premium_upcharge:8, coeff:0.10,
    desc:"Pad Thai, curries, street food"
  },
  "Chinese": {
    id:"chinese", base_aov:56, food_cost:27, bev_cost:20,
    drink_price:9, premium_upcharge:10, coeff:0.12,
    desc:"Cantonese, Sichuan, dim sum"
  },
  "Mediterranean": {
    id:"med", base_aov:84, food_cost:30, bev_cost:24,
    drink_price:12, premium_upcharge:12, coeff:0.14,
    desc:"Greek, Turkish, Lebanese, Spanish"
  }
};

// ═══════════════════════════════════════════
// STATE
// ═══════════════════════════════════════════
let selectedFormat = "Casual Dining";
let selectedCuisine = "Korean";

// ═══════════════════════════════════════════
// BUILD SELECTORS
// ═══════════════════════════════════════════
function buildSelectors() {
  const fg = document.getElementById('format-grid');
  const cg = document.getElementById('cuisine-grid');

  Object.keys(FORMATS).forEach(name => {
    const btn = document.createElement('button');
    btn.className = 'selector-btn' + (name === selectedFormat ? ' active' : '');
    btn.textContent = name;
    btn.onclick = () => { selectedFormat = name; refreshSelectors(); applyDefaults(); };
    fg.appendChild(btn);
  });

  Object.keys(CUISINES).forEach(name => {
    const btn = document.createElement('button');
    btn.className = 'selector-btn' + (name === selectedCuisine ? ' active' : '');
    btn.textContent = name;
    btn.onclick = () => { selectedCuisine = name; refreshSelectors(); applyDefaults(); };
    cg.appendChild(btn);
  });
}

function refreshSelectors() {
  document.querySelectorAll('#format-grid .selector-btn').forEach(btn => {
    btn.classList.toggle('active', btn.textContent === selectedFormat);
  });
  document.querySelectorAll('#cuisine-grid .selector-btn').forEach(btn => {
    btn.classList.toggle('active', btn.textContent === selectedCuisine);
  });

  const summary = document.getElementById('selection-summary');
  const fmt = FORMATS[selectedFormat];
  const cui = CUISINES[selectedCuisine];
  summary.className = 'selection-summary visible';
  summary.innerHTML = \`<strong>$\{selectedFormat}</strong> + <strong>$\{selectedCuisine}</strong> — $\{cui.desc}. Defaults loaded: ~$$\{Math.round(cui.base_aov * fmt.aov_mult)} avg check, $\{cui.food_cost + fmt.food_cost_add}% food cost, $\{cui.bev_cost + fmt.bev_cost_add}% bev cost.\`;
}

function applyDefaults() {
  const fmt = FORMATS[selectedFormat];
  const cui = CUISINES[selectedCuisine];

  const aov = Math.round(cui.base_aov * fmt.aov_mult);
  const food_cost = Math.max(15, cui.food_cost + fmt.food_cost_add);
  const bev_cost = Math.max(10, cui.bev_cost + fmt.bev_cost_add);
  const drink_price = Math.max(5, cui.drink_price + fmt.drink_price_add);

  setInput('shifts_mo', fmt.shifts_mo);
  setInput('tables_shift', fmt.tables_shift);
  setInput('party_size', fmt.party_size);
  setInput('current_aov', aov);
  setInput('tip_pct', fmt.tip_pct);
  setInput('coeff_per_pp', cui.coeff);
  setInput('current_quality', 36.5);
  setInput('target_quality', 55.0);
  setInput('current_drink_promos', fmt.current_drink_promos);
  setInput('target_drink_promos', fmt.target_drink_promos);
  setInput('drink_realization', fmt.drink_realization);
  setInput('rev_per_promo', drink_price);
  setInput('bespoke_cost', 50);
  setInput('servers_per_loc', fmt.servers_per_loc);
  setInput('num_locations', 1);
  setInput('food_cost_pct', food_cost);
  setInput('bev_cost_pct', bev_cost);

  recalc();
}

function setInput(id, val) {
  const el = document.getElementById(id);
  if (el) {
    // Round to appropriate precision
    if (typeof val === 'number') {
      if (Number.isInteger(val)) el.value = val;
      else el.value = parseFloat(val.toFixed(4));
    } else {
      el.value = val;
    }
  }
}

// ═══════════════════════════════════════════
// CALCULATION ENGINE
// ═══════════════════════════════════════════
function getVal(id) {
  return parseFloat(document.getElementById(id).value) || 0;
}

function fmt$(v) {
  if (Math.abs(v) >= 1000) return '$' + Math.round(v).toLocaleString();
  if (Math.abs(v) >= 100) return '$' + v.toFixed(0);
  return '$' + v.toFixed(2);
}

function recalc() {
  // Inputs
  const shifts_mo = getVal('shifts_mo');
  const tables_shift = getVal('tables_shift');
  const party_size = getVal('party_size');
  const current_aov = getVal('current_aov');
  const tip_pct = getVal('tip_pct') / 100;
  const coeff = getVal('coeff_per_pp');
  const current_q = getVal('current_quality');
  const target_q = getVal('target_quality');
  const cur_drinks = getVal('current_drink_promos');
  const tgt_drinks = getVal('target_drink_promos');
  const drink_real = getVal('drink_realization') / 100;
  const rev_promo = getVal('rev_per_promo');
  const bespoke_cost = getVal('bespoke_cost');
  const servers_loc = getVal('servers_per_loc');
  const num_locs = getVal('num_locations');
  const food_cost = getVal('food_cost_pct') / 100;
  const bev_cost = getVal('bev_cost_pct') / 100;

  // Computed: Restaurant
  const per_person = party_size > 0 ? current_aov / party_size : 0;
  document.getElementById('per_person_spend').textContent = fmt$(per_person);

  // Computed: Food Upsell
  const full_coeff = coeff * 100;
  document.getElementById('full_coeff').textContent = fmt$(full_coeff);

  const improvement_pp = target_q - current_q;
  document.getElementById('quality_improvement').textContent = improvement_pp.toFixed(1) + ' pp';

  const food_aov_lift = improvement_pp * coeff;
  document.getElementById('food_aov_lift').textContent = fmt$(food_aov_lift);

  // Computed: Drink Promo
  const extra_drinks = (tgt_drinks - cur_drinks) * drink_real;
  document.getElementById('extra_drink_promos').textContent = extra_drinks.toFixed(1);

  const extra_drink_rev = extra_drinks * rev_promo;
  document.getElementById('extra_drink_rev_shift').textContent = fmt$(extra_drink_rev);

  // Computed: Waterfall (per server)
  const food_table = food_aov_lift;
  const food_shift = food_table * tables_shift;
  const drink_shift = extra_drink_rev;
  const combined_shift = food_shift + drink_shift;
  const server_mo = combined_shift * shifts_mo;
  const server_yr = server_mo * 12;

  document.getElementById('wf_food_table').textContent = fmt$(food_table);
  document.getElementById('wf_food_shift').textContent = fmt$(food_shift);
  document.getElementById('wf_drink_shift').textContent = fmt$(drink_shift);
  document.getElementById('wf_combined_shift').textContent = fmt$(combined_shift);
  document.getElementById('wf_server_mo').textContent = fmt$(server_mo);
  document.getElementById('wf_server_yr').textContent = fmt$(server_yr);

  // Tips
  const tips_shift = combined_shift * tip_pct;
  const tips_mo = tips_shift * shifts_mo;
  const tips_yr = tips_mo * 12;
  document.getElementById('tips_shift').textContent = fmt$(tips_shift);
  document.getElementById('tips_mo').textContent = fmt$(tips_mo);
  document.getElementById('tips_yr').textContent = fmt$(Math.round(tips_yr));

  // Per-server ROI
  const net_server_mo = server_mo - bespoke_cost;
  const server_roi = bespoke_cost > 0 ? server_mo / bespoke_cost : 0;
  document.getElementById('net_per_server_mo').textContent = fmt$(net_server_mo);
  document.getElementById('per_server_roi').textContent = server_roi.toFixed(1) + 'x';

  // Scale-up
  const total_servers = servers_loc * num_locs;
  document.getElementById('total_servers').textContent = Math.round(total_servers).toLocaleString();

  const chain_mo_rev = server_mo * total_servers;
  const chain_yr_rev = chain_mo_rev * 12;
  const chain_cost = bespoke_cost * total_servers;
  const chain_net_mo = chain_mo_rev - chain_cost;
  const chain_net_yr = chain_net_mo * 12;
  const chain_roi = chain_cost > 0 ? chain_mo_rev / chain_cost : 0;

  document.getElementById('chain_mo_rev').textContent = fmt$(chain_mo_rev);
  document.getElementById('chain_yr_rev').textContent = fmt$(chain_yr_rev);
  document.getElementById('chain_cost').textContent = fmt$(chain_cost);
  document.getElementById('chain_net_mo').textContent = fmt$(chain_net_mo);
  document.getElementById('chain_net_yr').textContent = fmt$(chain_net_yr);
  document.getElementById('chain_roi').textContent = chain_roi.toFixed(1) + 'x';

  // Floating panel mirrors
  document.getElementById('fl_mo_rev').textContent = fmt$(chain_mo_rev);
  document.getElementById('fl_yr_rev').textContent = fmt$(chain_yr_rev);
  document.getElementById('fl_cost').textContent = fmt$(chain_cost);
  document.getElementById('fl_net_mo').textContent = fmt$(chain_net_mo);
  document.getElementById('fl_net_yr').textContent = fmt$(chain_net_yr);
  document.getElementById('fl_roi').textContent = chain_roi.toFixed(1) + 'x';
  document.getElementById('fl_tips_mo').textContent = fmt$(tips_mo);
  document.getElementById('fl_tips_yr').textContent = fmt$(Math.round(tips_yr));

  // Margin-adjusted
  const food_rev_mo = food_shift * shifts_mo * total_servers;
  const drink_rev_mo = drink_shift * shifts_mo * total_servers;
  const food_gp = food_rev_mo * (1 - food_cost);
  const drink_gp = drink_rev_mo * (1 - bev_cost);
  const combined_gp = food_gp + drink_gp;
  const net_profit = combined_gp - chain_cost;
  const margin_roi = chain_cost > 0 ? combined_gp / chain_cost : 0;
  const annual_np = net_profit * 12;

  document.getElementById('food_gp').textContent = fmt$(food_gp);
  document.getElementById('drink_gp').textContent = fmt$(drink_gp);
  document.getElementById('combined_gp').textContent = fmt$(combined_gp);
  document.getElementById('margin_bespoke_cost').textContent = '(' + fmt$(chain_cost) + ')';
  document.getElementById('net_profit').textContent = fmt$(net_profit);
  document.getElementById('margin_roi').textContent = margin_roi.toFixed(1) + 'x';
  document.getElementById('annual_net_profit').textContent = fmt$(annual_np);
}

// ═══════════════════════════════════════════
// INIT
// ═══════════════════════════════════════════
document.addEventListener('DOMContentLoaded', () => {
  buildSelectors();
  refreshSelectors();
  applyDefaults();

  // Attach recalc to all inputs
  document.querySelectorAll('input[type="number"]').forEach(input => {
    input.addEventListener('input', recalc);
    input.addEventListener('change', recalc);
  });
  // ═══════════════════════════════════════════
  // FLOATING STATS PANEL
  // ═══════════════════════════════════════════
  const scaleSection = document.getElementById('scale-up-section');
  const floatingPanel = document.getElementById('floating-stats');

  if (scaleSection && floatingPanel) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Show floating panel when scale-up section is NOT visible
        if (entry.isIntersecting) {
          floatingPanel.classList.remove('visible');
        } else {
          floatingPanel.classList.add('visible');
        }
      });
    }, { threshold: 0.1 });
    observer.observe(scaleSection);
  }
});
</script>

<!-- Floating Stats Panel -->
<div class="floating-stats" id="floating-stats">
  <div class="floating-stats-inner">
    <div class="float-card">
      <div class="fl-label">Monthly Rev Uplift</div>
      <div class="fl-value" id="fl_mo_rev">$12,530</div>
    </div>
    <div class="float-card">
      <div class="fl-label">Annual Rev Uplift</div>
      <div class="fl-value" id="fl_yr_rev">$150,359</div>
    </div>
    <div class="float-card">
      <div class="fl-label">Monthly Cost</div>
      <div class="fl-value fl-blue" id="fl_cost">$1,700</div>
    </div>
    <div class="float-card">
      <div class="fl-label">Net Monthly Gain</div>
      <div class="fl-value" id="fl_net_mo">$10,830</div>
    </div>
    <div class="float-card">
      <div class="fl-label">Annual Net Gain</div>
      <div class="fl-value" id="fl_net_yr">$129,959</div>
    </div>
    <div class="float-card">
      <div class="fl-label">Revenue ROI</div>
      <div class="fl-value fl-amber" id="fl_roi">7.4x</div>
    </div>
    <div class="float-divider"></div>
    <div class="float-card">
      <div class="fl-label">Tips / Server / Mo</div>
      <div class="fl-value fl-purple" id="fl_tips_mo">$66</div>
    </div>
    <div class="float-card">
      <div class="fl-label">Tips / Server / Yr</div>
      <div class="fl-value fl-purple" id="fl_tips_yr">$796</div>
    </div>
  </div>
</div>

</body>
</html>
`;

export default bespokeRoiCalculatorHtml;
